import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import SectionLabel from '@/components/landing/section-label'
import { TESTIMONIALS } from '@/constants/landing'

const CONTROL = 'grid size-12 cursor-pointer place-items-center rounded-full border-2 border-foreground transition-colors hover:bg-foreground hover:text-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground md:size-14'

const pad = (value: number): string => String(value).padStart(2, '0')

const TestimonialsSection: FC = (): ReactNode => {
    const [activeIndex, setActiveIndex] = useState(0)
    const total = TESTIMONIALS.length
    const active = TESTIMONIALS[activeIndex]

    const goTo = (index: number) => setActiveIndex((index + total) % total)

    return (
        <section aria-labelledby='testimonials-title' className='bg-primary text-primary-foreground'>
            <div className='site-container py-24 md:py-32'>
                <div className='flex flex-wrap items-center justify-between gap-6'>
                    <SectionLabel index='05'>
                        Kind words
                    </SectionLabel>

                    <h2 id='testimonials-title' className='sr-only'>
                        What our clients say
                    </h2>

                    <p aria-hidden='true' className='font-mono text-sm font-medium tabular-nums'>
                        {pad(activeIndex + 1)} / {pad(total)}
                    </p>
                </div>

                <div aria-live='polite' className='mt-10 min-h-[22rem] md:mt-14 md:min-h-[24rem] lg:min-h-[26rem]'>
                    <figure
                        key={active.name}
                        className='motion-safe:animate-in fade-in slide-in-from-bottom-6 duration-500 ease-out'
                    >
                        <blockquote>
                            <p className='max-w-6xl font-heading text-[clamp(2rem,4.8vw,4.75rem)] leading-[1.02] font-extrabold tracking-[-0.02em] text-pretty wdth-85'>
                                <span aria-hidden='true'>“</span>
                                {active.quote}
                                <span aria-hidden='true'>”</span>
                            </p>
                        </blockquote>

                        <figcaption className='mt-10 flex items-center gap-4'>
                            <span className='relative size-16 shrink-0 overflow-hidden rounded-full border-2 border-foreground bg-background md:size-20'>
                                <Image src={active.image} alt='' fill sizes='80px' className='object-cover' />
                            </span>

                            <span>
                                <span className='block font-heading text-xl leading-tight font-extrabold uppercase wdth-90 md:text-2xl'>
                                    {active.name}
                                </span>

                                <span className='block text-sm font-medium md:text-base'>
                                    {active.role}, {active.company}
                                </span>
                            </span>
                        </figcaption>
                    </figure>
                </div>

                <div className='mt-12 flex items-center justify-between gap-6 border-t-2 border-foreground pt-6'>
                    <div className='flex items-center'>
                        {TESTIMONIALS.map((testimonial, index) => (
                            <button
                                key={testimonial.name}
                                type='button'
                                onClick={() => goTo(index)}
                                aria-label={`Show the quote from ${testimonial.name}`}
                                aria-current={index === activeIndex ? 'true' : undefined}
                                className='group grid h-10 min-w-10 cursor-pointer place-items-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-foreground'
                            >
                                <span
                                    className={cn(
                                        'block h-3 rounded-full bg-foreground transition-all duration-300',
                                        index === activeIndex ? 'w-8' : 'w-3 opacity-30 group-hover:opacity-70'
                                    )}
                                />
                            </button>
                        ))}
                    </div>

                    <div className='flex gap-3'>
                        <button
                            type='button'
                            onClick={() => goTo(activeIndex - 1)}
                            aria-label='Previous quote'
                            className={CONTROL}
                        >
                            <ArrowLeft aria-hidden='true' className='size-5' />
                        </button>

                        <button
                            type='button'
                            onClick={() => goTo(activeIndex + 1)}
                            aria-label='Next quote'
                            className={CONTROL}
                        >
                            <ArrowRight aria-hidden='true' className='size-5' />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default TestimonialsSection
